import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  alpha,
  Box,
  Stack,
  Typography,
  Button,
  ButtonBase,
  Drawer,
  IconButton,
  Switch,
  TextField,
  MenuItem,
  useTheme,
} from 'omaha-design-system';
import { X as CloseIcon, SlidersHorizontal as TuneIcon, CaretRight as ChevronRightIcon, MagnifyingGlass as SearchIcon, FirstAidKit as InjuryIcon, ListNumbers as LineupsIcon, Lock as LockIcon } from '@phosphor-icons/react';
import { usePropsAccess } from '../hooks/usePropsAccess';
import { IRootState } from '../../../store/reducer';
import {
  setMinMinutes,
  setProjectionWeights,
  setPlayerSearch,
  setDrawerFilters,
} from '../store/slice';
import { usePropsData } from '../hooks/usePropsData';
import { leagueHasMinutes, leagueHasPositionFilter } from '../data/leagueConfig';
import { CustomizeBlendDialog } from './CustomizeBlendDialog';
import PropsStateDropdown from './PropsStateDropdown';
import PropsSportsbookDropdown from './PropsSportsbookDropdown';

interface FilterBottomSheetProps {
  open: boolean;
  onClose: () => void;
  onOpenInjuryReport?: () => void;
  onOpenLineups?: () => void;
}

export const FilterBottomSheet: React.FC<FilterBottomSheetProps> = ({ open, onClose, onOpenInjuryReport, onOpenLineups }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const propsState = useSelector((s: IRootState) => s.stokasticProps);
  const { filters } = propsState;
  const { lastUpdatedTime, availablePositions } = usePropsData();

  const inputSx = {
    '& .MuiInputBase-root': {
      bgcolor: alpha(theme.palette.common.white, 0.05),
      borderRadius: '4px',
      height: 40,
      '& input': {
        py: 1,
        px: 1.5,
        fontSize: '14px',
        '&::placeholder': {
          color: alpha(theme.palette.common.white, 0.4),
          opacity: 1,
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.common.white, 0.1),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.common.white, 0.2),
    },
  };

  // Local state for form values (show empty string when value is 0)
  const [localMinEv, setLocalMinEv] = useState<string>(filters.minEdge ? String(filters.minEdge) : '');
  const [localMaxEv, setLocalMaxEv] = useState<string>(filters.maxEV ? String(filters.maxEV) : '');
  const [localMinOdds, setLocalMinOdds] = useState<string>(filters.minOdds ? String(filters.minOdds) : '');
  const [localMaxOdds, setLocalMaxOdds] = useState<string>(filters.maxOdds ? String(filters.maxOdds) : '');
  const [localMinMinutes, setLocalMinMinutes] = useState<string>(filters.minMinutes ? String(filters.minMinutes) : '');
  const [localPlayerSearch, setLocalPlayerSearch] = useState<string>(filters.playerSearch);
  const [localHideInjuredTeammate, setLocalHideInjuredTeammate] = useState(filters.hideInjuredTeammate);
  const [localRequireBothModelsEV, setLocalRequireBothModelsEV] = useState(filters.requireBothModelsEV);
  const [localPosition, setLocalPosition] = useState<string>(filters.positionFilter || 'All');
  // A persisted/stale position that the current feed doesn't offer falls back to All, for display and apply alike
  const effectivePosition = localPosition === 'All' || availablePositions.includes(localPosition) ? localPosition : 'All';
  const [customizeBlendOpen, setCustomizeBlendOpen] = useState(false);
  // Projection blending is a subscriber feature; teaser viewers see it locked
  const { isTeaser } = usePropsAccess();

  // Sync local state only when the sheet opens (not on every filters change,
  // which would reset local toggles when e.g. blend weights are applied mid-edit)
  useEffect(() => {
    if (open) {
      setLocalMinEv(filters.minEdge ? String(filters.minEdge) : '');
      setLocalMaxEv(filters.maxEV ? String(filters.maxEV) : '');
      setLocalMinOdds(filters.minOdds ? String(filters.minOdds) : '');
      setLocalMaxOdds(filters.maxOdds ? String(filters.maxOdds) : '');
      setLocalMinMinutes(filters.minMinutes ? String(filters.minMinutes) : '');
      setLocalPlayerSearch(filters.playerSearch);
      setLocalHideInjuredTeammate(filters.hideInjuredTeammate);
      setLocalRequireBothModelsEV(filters.requireBothModelsEV);
      setLocalPosition(filters.positionFilter || 'All');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleApply = () => {
    dispatch(setDrawerFilters({
      minEdge: parseFloat(localMinEv) || 0,
      maxEV: parseFloat(localMaxEv) || 0,
      minOdds: parseFloat(localMinOdds) || 0,
      maxOdds: parseFloat(localMaxOdds) || 0,
      hideInjuredTeammate: localHideInjuredTeammate,
      requireBothModelsEV: localRequireBothModelsEV,
      positionFilter: effectivePosition,
    }));
    dispatch(setMinMinutes(parseFloat(localMinMinutes) || 0));
    dispatch(setPlayerSearch(localPlayerSearch));
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: '#343536',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: 'none',
        }}
      >
        {/* Handle */}
        <Box
          sx={{
            width: 40,
            height: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: 2,
            margin: '12px auto 8px',
            flexShrink: 0,
          }}
        />

        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, flexShrink: 0 }}
        >
          <Typography
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '24px',
              color: '#F2F2F3',
            }}
          >
            Filters
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#F2F2F3' }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 1 }}>
          <Stack spacing={3}>
          {/* Quick Actions — Injury Report (NBA) / Lineups (MLB) */}
          {(onOpenInjuryReport || onOpenLineups) && (
            <Stack spacing={1}>
              {onOpenInjuryReport && (
                <ButtonBase
                  onClick={() => {
                    onClose();
                    onOpenInjuryReport();
                  }}
                  sx={{
                    height: 52,
                    backgroundColor: '#2A2B2C',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    '&:hover': { backgroundColor: '#333435' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <InjuryIcon size={20} color="#EF5350" />
                    <Typography
                      sx={{
                        fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#F2F2F3',
                      }}
                    >
                      Injury Report
                    </Typography>
                  </Stack>
                  <ChevronRightIcon size={20} color="rgba(242, 242, 243, 0.5)" />
                </ButtonBase>
              )}
              {onOpenLineups && (
                <ButtonBase
                  onClick={() => {
                    onClose();
                    onOpenLineups();
                  }}
                  sx={{
                    height: 52,
                    backgroundColor: '#2A2B2C',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    '&:hover': { backgroundColor: '#333435' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <LineupsIcon size={20} color="#00B553" />
                    <Typography
                      sx={{
                        fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#F2F2F3',
                      }}
                    >
                      MLB Lineups
                    </Typography>
                  </Stack>
                  <ChevronRightIcon size={20} color="rgba(242, 242, 243, 0.5)" />
                </ButtonBase>
              )}
            </Stack>
          )}

          {/* State & Sportsbooks Section */}
          <Stack spacing={1.5}>
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              State & Sportsbooks
            </Typography>

            <Stack direction="row" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { width: '100%', minWidth: 'unset' } }}>
                <Typography
                  sx={{
                    fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '12px',
                    color: 'rgba(242, 242, 243, 0.5)',
                    mb: 0.5,
                  }}
                >
                  State
                </Typography>
                <PropsStateDropdown size="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { width: '100%', minWidth: 'unset' } }}>
                <Typography
                  sx={{
                    fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '12px',
                    color: 'rgba(242, 242, 243, 0.5)',
                    mb: 0.5,
                  }}
                >
                  Sportsbooks
                </Typography>
                <PropsSportsbookDropdown size="small" />
              </Box>
            </Stack>
          </Stack>

          {/* Search Section */}
          <Stack spacing={1.5}>
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Search
            </Typography>

            <Box
              sx={{
                backgroundColor: '#2A2B2C',
                borderRadius: '8px',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <SearchIcon size={20} color="rgba(242, 242, 243, 0.5)" />
              <Box
                component="input"
                type="text"
                placeholder="Search player, team, stat..."
                value={localPlayerSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalPlayerSearch(e.target.value)}
                sx={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  color: '#F2F2F3',
                  '&::placeholder': {
                    color: 'rgba(242, 242, 243, 0.5)',
                  },
                }}
              />
            </Box>
          </Stack>

          {/* EV Range % */}
          <Stack spacing={1.5}>
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              EV Range %
            </Typography>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.5),
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Min
                </Typography>
                <TextField
                  placeholder="Any"
                  value={localMinEv}
                  onChange={(e) => setLocalMinEv(e.target.value)}
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.5),
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Max
                </Typography>
                <TextField
                  placeholder="Any"
                  value={localMaxEv}
                  onChange={(e) => setLocalMaxEv(e.target.value)}
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              </Box>
            </Stack>
          </Stack>

          {/* Odds Range */}
          <Stack spacing={1.5}>
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Odds Range
            </Typography>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.5),
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Min
                </Typography>
                <TextField
                  placeholder="e.g. -200"
                  value={localMinOdds}
                  onChange={(e) => setLocalMinOdds(e.target.value)}
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.5),
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Max
                </Typography>
                <TextField
                  placeholder="e.g. +300"
                  value={localMaxOdds}
                  onChange={(e) => setLocalMaxOdds(e.target.value)}
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              </Box>
            </Stack>
          </Stack>

          {/* Position (NFL) */}
          {leagueHasPositionFilter(filters.league) && availablePositions.length > 0 && (
            <Stack spacing={1.5}>
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                Position
              </Typography>
              <TextField
                select
                value={effectivePosition}
                onChange={(e) => setLocalPosition(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={inputSx}
              >
                {['All', ...availablePositions].map((pos) => (
                  <MenuItem key={pos} value={pos}>
                    {pos === 'All' ? 'All Positions' : pos}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}

          {/* Qualifiers */}
          {leagueHasMinutes(filters.league) && (
            <Stack spacing={1.5}>
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                Qualifiers
              </Typography>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.5),
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Min Player Minutes
                </Typography>
                <TextField
                  placeholder="Any"
                  value={localMinMinutes}
                  onChange={(e) => setLocalMinMinutes(e.target.value)}
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              </Box>
            </Stack>
          )}

          {/* Player Filters Section — the injured-teammate switch relies on projected minutes (NBA/NHL only) */}
          {leagueHasMinutes(filters.league) && (
            <Stack spacing={1.5}>
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                Player Filters
              </Typography>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Box>
                  <Typography sx={{ fontFamily: '"Gilroy", "Instrument Sans", sans-serif', fontWeight: 500, fontSize: '14px', color: '#F2F2F3' }}>
                    Hide Questionable Impact
                  </Typography>
                  <Typography sx={{ fontFamily: '"Gilroy", "Instrument Sans", sans-serif', fontWeight: 400, fontSize: '12px', color: 'rgba(242, 242, 243, 0.5)' }}>
                    Remove bets where a teammate is listed as questionable
                  </Typography>
                </Box>
                <Switch
                  checked={localHideInjuredTeammate}
                  onChange={(e) => setLocalHideInjuredTeammate(e.target.checked)}
                  size="small"
                />
              </Stack>
            </Stack>
          )}

          {/* Model Filters Section */}
          <Stack spacing={1.5}>
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Model Filters
            </Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
              <Box>
                <Typography sx={{ fontFamily: '"Gilroy", "Instrument Sans", sans-serif', fontWeight: 500, fontSize: '14px', color: '#F2F2F3' }}>
                  Both Models +EV
                </Typography>
                <Typography sx={{ fontFamily: '"Gilroy", "Instrument Sans", sans-serif', fontWeight: 400, fontSize: '12px', color: 'rgba(242, 242, 243, 0.5)' }}>
                  Only show bets where both models are +EV
                </Typography>
              </Box>
              <Switch
                checked={localRequireBothModelsEV}
                onChange={(e) => setLocalRequireBothModelsEV(e.target.checked)}
                size="small"
              />
            </Stack>
          </Stack>

          {/* Projections Section */}
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                Projections
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                Updated: {lastUpdatedTime || '-'}
              </Typography>
            </Stack>

            <Stack spacing={1}>
              {/* Customize Blend Button */}
              <ButtonBase
                onClick={() => setCustomizeBlendOpen(true)}
                disabled={isTeaser}
                sx={{
                  height: 52,
                  backgroundColor: '#2A2B2C',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  opacity: isTeaser ? 0.6 : 1,
                  '&:hover': {
                    backgroundColor: isTeaser ? '#2A2B2C' : '#333435',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {isTeaser ? (
                    <LockIcon size={20} color="rgba(255, 255, 255, 0.3)" />
                  ) : (
                    <TuneIcon size={20} color="#42A5F5" />
                  )}
                  <Typography
                    sx={{
                      fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#F2F2F3',
                    }}
                  >
                    Customize Blend
                  </Typography>
                </Stack>
                {!isTeaser && <ChevronRightIcon size={20} color="rgba(242, 242, 243, 0.5)" />}
              </ButtonBase>
            </Stack>
          </Stack>

          </Stack>
        </Box>

        {/* Fixed Apply Button */}
        <Box sx={{ px: 2, pb: 3, pt: 2, flexShrink: 0 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleApply}
            sx={{
              height: 48,
              borderRadius: '8px',
              backgroundColor: '#42A5F5',
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              color: 'rgba(0, 0, 0, 0.87)',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>

      {/* Customize Blend Dialog */}
      {/* `&& !isTeaser`: a league switch can flip access while the dialog is open */}
      <CustomizeBlendDialog
        open={customizeBlendOpen && !isTeaser}
        onClose={() => setCustomizeBlendOpen(false)}
        stokWeight={filters.stokWeight}
        marketWeight={filters.marketWeight}
        onApply={(stokWeight, marketWeight) => {
          dispatch(setProjectionWeights({ stokWeight, marketWeight }));
        }}
      />
    </Drawer>
  );
};

export default FilterBottomSheet;
